/**
 * "View as", in the browser: which person's grants this tab is reading the app as.
 *
 * The browser only ASKS. Every request it sends while viewing as someone carries
 * `X-CPC-View-As`, and the API decides — full-access callers only, audited, read-only
 * (api/shared/viewAs.ts). Nothing here grants anything; a non-admin who set the value
 * by hand gets 403 on the first request, and `refused` below drops it.
 *
 * Kept in sessionStorage, so a reload stays in the view and a new tab or a closed
 * browser does not. Read once, at load: starting and exiting both reload the page, so
 * every fetcher, cache key and provider sees one value for the life of the page and
 * nothing can be half in one view and half in the other.
 */

export const VIEW_AS_HEADER = 'X-CPC-View-As'
const KEY = 'cpc.viewAs'

/** Server codes that mean "this tab must not be viewing as anyone". */
const REFUSED = new Set(['view-as-forbidden', 'view-as-invalid'])

function read(): string | null {
  try {
    const v = window.sessionStorage.getItem(KEY)?.trim().toLowerCase()
    return v ? v : null
  } catch {
    return null
  }
}

const current = typeof window === 'undefined' ? null : read()

/** The target's email while viewing as someone; null otherwise. */
export function viewingAs(): string | null {
  return current
}

/** Adds the view-as header, if any, to a request's headers. */
export function withViewAs(headers: Record<string, string>): Record<string, string> {
  if (current) headers[VIEW_AS_HEADER] = current
  return headers
}

/** A cache key that is never shared between the caller's own view and a view-as one. */
export function viewAsKey(base: string): string {
  return current ? `${base}|view-as:${current}` : base
}

/** Loose check before asking the server, which validates for real. */
export const looksLikeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())

function reload(): void {
  window.location.reload()
}

export function startViewAs(email: string): void {
  try {
    window.sessionStorage.setItem(KEY, email.trim().toLowerCase())
  } catch {
    return
  }
  reload()
}

export function exitViewAs(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    /* nothing stored, nothing to clear */
  }
  reload()
}

/** Forget the view without reloading — on sign-out, which navigates by itself. */
export function forgetViewAs(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Reads a failed response's error. If the server refused the view-as itself (the
 * caller is not full access, or the value is not an email), the stored view is dropped
 * and the page reloads as the caller — a non-admin is never left in a view. Returns the
 * server's message for anything else.
 */
export async function viewAsFailure(res: Response): Promise<string | null> {
  const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null
  if (current && body?.code && REFUSED.has(body.code)) {
    exitViewAs()
  }
  return body?.error ?? null
}
